/**
 * ERPs Submit Policy
 * 
 * Handles ERP registration:
 * - ERPs typically use API key/secret authentication
 * - Calls RegisterSalesChannelIntegration with credentials
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { ERPsState } from '../state';
import { ERPsCommand } from '../commands';
import { uuidv7 } from 'uuidv7';

/**
 * Creates a policy that handles ERP submission
 */
export const createERPsSubmitPolicy = (
  backend: ClientLibraryType
): Policy<ERPsState, ERPsCommand> => ({
  name: 'erps-submit',

  shouldReact: ( prev, next ) => {
    // React when modal transitions to 'connecting' step
    return (
      prev.addERPModal.step !== 'connecting' &&
      next.addERPModal.step === 'connecting' &&
      next.loading.submitting
    );
  },

  onStateChange: async ({ next, ctx }) => {
    const { addERPModal } = next;
    const { selectedType, formData } = addERPModal;
    
    if ( !selectedType ) {
      console.error( '[erps] No ERP type selected' );
      ctx.dispatch(
        { type: 'integrations/erps/loading/setSubmitting', payload: { isLoading: false }},
        { source: 'api' }
      );
      return;
    }

    // eslint-disable-next-line teselly/no-swallowed-command-dispatch -- catch resets submit loading + returns to the details step; the error itself is not surfaced -- genuine swallow, waived pending the separate rethrow-cleanup ticket (TV1-2010).
    try {
      // ERPs use API key/secret authentication
      await backend.commands.ProvisionSystemIntegration({
        id: uuidv7(),
        body: {
          connectorId: selectedType,
          credentials: {
            name: formData.name,
            apiKey: formData.apiKey,
            apiSecret: formData.apiSecret
          }
        } as any
      });

      // Success - close modal
      ctx.dispatch(
        { type: 'integrations/erps/addModal/close', payload: {}},
        { source: 'api' }
      );
      ctx.dispatch(
        { type: 'integrations/erps/loading/setSubmitting', payload: { isLoading: false }},
        { source: 'api' }
      );
    } catch ( error ) {
      ctx.dispatch(
        { type: 'integrations/erps/loading/setSubmitting', payload: { isLoading: false }},
        { source: 'api' }
      );
      ctx.dispatch(
        { type: 'integrations/erps/addModal/setStep', payload: { step: 'enterDetails' }},
        { source: 'api' }
      );
    }
  }
});
